import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarClock, ShieldCheck } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import TrialBanner from "@/components/TrialBanner";
import BusinessOnboarding from "@/components/BusinessOnboarding";
import BusinessBottomNav from "@/components/BusinessBottomNav";
import BusinessSettingsSheet from "@/components/BusinessSettingsSheet";

export default function DashboardLayout() {
  const { user, logout, stopImpersonate } = useAuth();
  const nav = useNavigate();
  const { data: biz } = useQuery({
    queryKey: ["business"],
    queryFn: () => api.get("/business").then((response) => response.data),
  });
  const { data: subscription, isLoading: subscriptionLoading } = useQuery({
    queryKey: ["subscription-status"],
    queryFn: () => api.get("/subscription/status").then((response) => response.data),
  });
  const needsOnboarding = biz && !(biz.name && biz.name.trim());
  const isPro = subscription?.subscription_state === "active";

  const doLogout = async () => { await logout(); nav("/giris"); };
  const backToAdmin = async () => { await stopImpersonate(); nav("/admin/isletmeler"); };

  return (
    <div className="business-panel-light min-h-screen bg-background text-foreground" translate="no">
      {needsOnboarding && <BusinessOnboarding />}
      {user?.impersonating && (
        <div className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-amber-400 px-4 py-2.5 text-amber-950" data-testid="impersonation-banner" translate="no">
          <div className="flex min-w-0 items-center gap-2">
            <ShieldCheck className="h-4 w-4 shrink-0" />
            <span className="truncate text-sm font-medium"><b>{user.business_name}</b> işletmesini yönetiyorsunuz</span>
          </div>
          <Button size="sm" variant="ghost" className="h-8 shrink-0 gap-1.5" onClick={backToAdmin} data-testid="back-to-admin-btn">
            <ArrowLeft className="h-4 w-4" /> Yönetime dön
          </Button>
        </div>
      )}
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5 sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <CalendarClock className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="truncate font-display text-xl font-bold" data-testid="business-name-header">
                {biz?.name || "365Rez"}
              </p>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {isPro ? "Pro işletme" : "Ücretsiz plan"}
              </p>
            </div>
          </div>
          <BusinessSettingsSheet businessName={biz?.name} onLogout={doLogout} />
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl px-5 pb-28 pt-6 sm:px-8 sm:pt-8" translate="no">
        <TrialBanner />
        <Outlet />
      </main>
      <BusinessBottomNav isPro={isPro} isLoading={subscriptionLoading} />
    </div>
  );
}
